/**
 * 创意工坊插件管理 Store
 */

import type { Message } from '@xsai/shared-chat'

import type {
  AIMessageProcessResult,
  ChatContext,
  ContactInfo,
  CustomMessageType,
  InstalledPlugin,
  MessageMetadata,
  Plugin,
  PluginAIActionDefinition,
  PluginContext,
  PluginLocation,
  PluginPackage,
  PluginPermission,
  PluginWindowOptions,
  RegisteredPluginAIAction,
  UserMessageProcessResult,
} from '../types/plugin'

import JSZip from 'jszip'

import { readableStreamToAsyncIterator } from '@moeru/std'
import {
  BasicInputFile,
  BasicTextarea,
  BidirectionalTransition,
  Checkbox,
  ColorHueRange,
  Combobox,
  FieldCheckbox,
  FieldInput,
  FieldKeyValues,
  FieldRange,
  FieldSelect,
  FieldValues,
  Input,
  InputFile,
  InputKeyValue,
  Radio,
  Range,
  RoundRange,
  Select,
  SelectOption,
  Textarea,
  TransitionHorizontal,
  TransitionVertical,
} from '@proj-airi/ui'
import { generateText } from '@xsai/generate-text'
import { streamText } from '@xsai/stream-text'
import { defineStore } from 'pinia'
import { computed, defineComponent, markRaw, ref, watch } from 'vue'

import PluginIcon from '../components/common/PluginIcon.vue'

import { getClaudeHeaders, injectCacheControl, isClaudeModel } from '../utils/claude-caching'
import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { showError, showInfo, showSuccess } from '../utils/toast'
import { useChatStore } from './chat'
import { useContactsStore } from './contacts'
import { useAiriCardStore } from './modules/airi-card'
import { useConsciousnessStore } from './modules/consciousness'
import { useProvidersStore } from './providers'
import { useStickersStore } from './stickers'
import { useUserPersonaStore } from './user-persona'
import { useWorldBookStore } from './worldbook'

type BeforeAIReplyHandler = (message: string, metadata: MessageMetadata) => AIMessageProcessResult | Promise<AIMessageProcessResult>
type BeforeUserMessageHandler = (message: string, metadata: MessageMetadata) => UserMessageProcessResult | Promise<UserMessageProcessResult>
type AfterUserMessageHandler = (message: string, metadata: MessageMetadata) => void | Promise<void>
type ContactSwitchHandler = (fromContactId: string | null, toContactId: string) => void | Promise<void>
type DynamicPromptGenerator = (context: ChatContext) => string | null
type PluginAIActionRegistry = Map<string, Map<string, PluginAIActionDefinition>>

const CONFIG_STORAGE_PREFIX = 'plugins/config/'
const GLOBAL_STORAGE_PREFIX = 'plugins/global/'
const PLUGIN_DATA_PREFIX = 'plugins/data/'
const PLUGIN_LOCATIONS = new Set<PluginLocation>(['explore', 'chat', 'chat-panel', 'profile', 'settings'])
const PLUGIN_WINDOW_MODES = new Set(['fullscreen', 'floating'])
const PLUGIN_WINDOW_POSITIONS = new Set(['bottom-right', 'bottom-left', 'top-right', 'top-left'])
const PLUGIN_WINDOW_SIZE_FIELDS = ['width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight'] as const
const PLUGIN_WINDOW_ALLOWED_WORDS = new Set(['calc', 'min', 'max', 'clamp', 'px', 'rem', 'em', 'vw', 'vh'])
const SUPPORTED_PLUGIN_API_VERSIONS = new Set(['1.0', '2.0'])
const DEFAULT_PLUGIN_API_VERSION = '2.0'

// 插件 context.fetch 敏感目标域名黑名单
// 1) AI provider 官方地址：防止恶意插件拿到 apiKey 直接外发 / 消耗用户额度
// 2) meowchat 自家服务：防止插件越权调用 license / push / plugin-market 等后端
const PLUGIN_FETCH_BLOCKED_SUFFIXES = [
  // AI providers
  'openai.com',
  'api.anthropic.com',
  'anthropic.com',
  'api.minimax.io',
  'api.minimaxi.com',
  'minimax.chat',
  'api.fish.audio',
  'fish.audio',
  'api.deepseek.com',
  'api.moonshot.cn',
  'dashscope.aliyuncs.com',
  'generativelanguage.googleapis.com',
  'api.x.ai',
  'api.groq.com',
  'api.together.xyz',
  'api.cohere.ai',
  'api.mistral.ai',
  'api.siliconflow.cn',
  'siliconflow.cn',
  'ark.cn-beijing.volces.com',
  'volces.com',
  'api.302.ai',
  'openrouter.ai',
  // meowchat 自家
  'chat.nktp.top',
  'nktp.top',
]

export function isBlockedFetchTarget(url: string): boolean {
  try {
    const u = new URL(url)
    if (u.protocol !== 'http:' && u.protocol !== 'https:')
      return false
    const host = u.hostname.toLowerCase()
    return PLUGIN_FETCH_BLOCKED_SUFFIXES.some(s => host === s || host.endsWith(`.${s}`))
  }
  catch {
    return false
  }
}

// callAI 每插件限流：1 分钟 20 次（正常对话场景远低于此）
const CALL_AI_WINDOW_MS = 60_000
const CALL_AI_MAX_PER_WINDOW = 20
const callAICounters = new Map<string, number[]>()

export function callAIRateCheck(pluginId: string): void {
  const now = Date.now()
  const arr = callAICounters.get(pluginId) || []
  const pruned = arr.filter(t => now - t < CALL_AI_WINDOW_MS)
  if (pruned.length >= CALL_AI_MAX_PER_WINDOW) {
    throw new Error(`[callAI] 插件 ${pluginId} 调用频率过高（${CALL_AI_MAX_PER_WINDOW} 次/分钟），请稍后再试`)
  }
  pruned.push(now)
  callAICounters.set(pluginId, pruned)
}

export function sanitizePluginWindowSize(value: unknown): string | undefined {
  if (typeof value !== 'string')
    return undefined

  const size = value.trim()
  if (!size || size.length > 120)
    return undefined

  const lower = size.toLowerCase()
  if (
    /[;:{}[\]'"`\\@*]/.test(size)
    || lower.includes('url(')
    || lower.includes('var(')
    || lower.includes('attr(')
    || lower.includes('expression(')
    || lower.includes('import')
  ) {
    return undefined
  }

  if (!/^[\w\s().,%+\-/]+$/.test(size))
    return undefined

  const words = lower.match(/[a-z]+/g) || []
  if (words.some(word => !PLUGIN_WINDOW_ALLOWED_WORDS.has(word)))
    return undefined

  return size
}

export function normalizePluginLocations(value: unknown, fallback: PluginLocation[] = ['chat-panel']): PluginLocation[] {
  const locations = Array.isArray(value) ? value : fallback
  const normalized = locations.filter((item): item is PluginLocation => typeof item === 'string' && PLUGIN_LOCATIONS.has(item as PluginLocation))
  return normalized.length ? normalized : fallback
}

export function normalizePluginPermissions(value: unknown): PluginPermission[] {
  if (!Array.isArray(value))
    return []

  const permissions = value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map(item => item.trim() as PluginPermission)
  return Array.from(new Set(permissions))
}

// 极简 semver 比较：返回 1 / 0 / -1（a 相对 b）。支持 X.Y.Z、X.Y.Z-suffix、以及 `v` 前缀；
// 非法输入按 0.0.0 处理。用途：客户端判断「本地版本是否严格低于广场版本」以避免更新死循环。
// 注：预发行标签（-beta 等）会被忽略，仅比较 X.Y.Z，不适合严格版本排序。
export function compareSemver(a: string, b: string): number {
  const parse = (v: string): [number, number, number] => {
    const stripped = (v || '0.0.0').replace(/^v/i, '')
    const [core] = stripped.split(/[-+]/)
    const parts = core.split('.').map(p => Number.parseInt(p, 10))
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0]
  }
  const pa = parse(a)
  const pb = parse(b)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i])
      return pa[i] < pb[i] ? -1 : 1
  }
  return 0
}

function hasPluginPermission(plugin: Plugin | PluginPackage, permission: PluginPermission): boolean {
  return normalizePluginPermissions(plugin.metadata.permissions).includes(permission)
}

export function normalizePluginWindowOptions(value: unknown): PluginWindowOptions | undefined {
  if (!value || typeof value !== 'object')
    return undefined

  const raw = value as Record<string, unknown>
  const normalized: PluginWindowOptions = {}

  if (typeof raw.mode === 'string' && PLUGIN_WINDOW_MODES.has(raw.mode))
    normalized.mode = raw.mode as PluginWindowOptions['mode']

  if (typeof raw.position === 'string' && PLUGIN_WINDOW_POSITIONS.has(raw.position))
    normalized.position = raw.position as PluginWindowOptions['position']

  for (const field of PLUGIN_WINDOW_SIZE_FIELDS) {
    const size = sanitizePluginWindowSize(raw[field])
    if (size)
      normalized[field] = size
  }

  if (typeof raw.minimizable === 'boolean')
    normalized.minimizable = raw.minimizable
  if (typeof raw.draggable === 'boolean')
    normalized.draggable = raw.draggable
  if (typeof raw.rememberPosition === 'boolean')
    normalized.rememberPosition = raw.rememberPosition

  return Object.keys(normalized).length > 0 ? normalized : undefined
}

function normalizePluginApiVersion(value: unknown): string {
  if (typeof value !== 'string')
    return DEFAULT_PLUGIN_API_VERSION

  const version = value.trim()
  return version || DEFAULT_PLUGIN_API_VERSION
}

function assertSupportedPluginApiVersion(value: unknown, pluginName: string): string {
  const version = normalizePluginApiVersion(value)
  if (SUPPORTED_PLUGIN_API_VERSIONS.has(version))
    return version

  throw new Error(`插件 ${pluginName} 需要 API 版本 ${version}，但当前只支持 ${Array.from(SUPPORTED_PLUGIN_API_VERSIONS).join(', ')}`)
}

function normalizePluginPackage(pluginPackage: PluginPackage): PluginPackage {
  return {
    ...pluginPackage,
    metadata: {
      ...pluginPackage.metadata,
      api: normalizePluginApiVersion(pluginPackage.metadata.api),
      permissions: normalizePluginPermissions(pluginPackage.metadata.permissions),
      location: normalizePluginLocations(pluginPackage.metadata.location),
      window: normalizePluginWindowOptions(pluginPackage.metadata.window),
    },
  }
}

/**
 * 插件 KV 存储：IndexedDB 后端 + 内存 Map 缓存
 * - get/set/remove 同步操作内存缓存（保持原有同步 API 不变）
 * - 写入异步落盘到 IndexedDB（防抖 100ms 批量写入）
 * - 初始化时自动从 localStorage 迁移旧数据
 * - 容量从 localStorage 的 ~5MB 提升到 IndexedDB 的几百 MB
 */
class PluginKVStore {
  private cache = new Map<string, unknown>()
  private db: IDBDatabase | null = null
  private _ready: Promise<void>
  private writeQueue = new Map<string, unknown | null>()
  private flushTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private dbName = 'meowchat-plugin-storage', private storeName = 'kv') {
    this._ready = this.init()
  }

  private init(): Promise<void> {
    return new Promise((resolve) => {
      try {
        const request = indexedDB.open(this.dbName, 1)
        request.onupgradeneeded = () => {
          const db = request.result
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName)
          }
        }
        request.onsuccess = () => {
          this.db = request.result
          const tx = this.db.transaction(this.storeName, 'readonly')
          const store = tx.objectStore(this.storeName)
          const getAllKeys = store.getAllKeys()
          const getAllValues = store.getAll()
          tx.oncomplete = () => {
            const keys = getAllKeys.result as string[]
            const values = getAllValues.result
            for (let i = 0; i < keys.length; i++) {
              this.cache.set(keys[i], values[i])
            }
            this.migrateFromLocalStorage()
            resolve()
          }
          tx.onerror = () => {
            console.warn('⚠️ 插件存储：IndexedDB 读取失败，降级为纯内存模式')
            this.migrateFromLocalStorage()
            resolve()
          }
        }
        request.onerror = () => {
          console.warn('⚠️ 插件存储：IndexedDB 不可用，降级为纯内存模式')
          this.fallbackToLocalStorage()
          resolve()
        }
      }
      catch {
        console.warn('⚠️ 插件存储：IndexedDB 不可用，降级为纯内存模式')
        this.fallbackToLocalStorage()
        resolve()
      }
    })
  }

  /** IndexedDB 完全不可用时，从 localStorage 加载数据到内存（只读降级） */
  private fallbackToLocalStorage(): void {
    const prefixes = [CONFIG_STORAGE_PREFIX, GLOBAL_STORAGE_PREFIX, PLUGIN_DATA_PREFIX]
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && prefixes.some(p => key.startsWith(p))) {
        try {
          const raw = localStorage.getItem(key)
          if (raw != null) {
            this.cache.set(key, JSON.parse(raw))
          }
        }
        catch {}
      }
    }
  }

  private migrateFromLocalStorage(): void {
    const prefixes = [CONFIG_STORAGE_PREFIX, GLOBAL_STORAGE_PREFIX, PLUGIN_DATA_PREFIX]
    const keysToMigrate: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && prefixes.some(p => key.startsWith(p))) {
        keysToMigrate.push(key)
      }
    }
    if (keysToMigrate.length === 0)
      return

    const migratedKeys: string[] = []
    for (const key of keysToMigrate) {
      if (this.cache.has(key)) {
        migratedKeys.push(key)
        continue
      }

      try {
        const raw = localStorage.getItem(key)
        if (raw == null)
          continue

        const value = JSON.parse(raw)
        this.cache.set(key, value)
        this.writeQueue.set(key, value)
        migratedKeys.push(key)
      }
      catch (error) {
        console.warn(`⚠️ 插件存储项 ${key} 解析失败，保留 localStorage 数据`, error)
      }
    }

    for (const key of migratedKeys)
      localStorage.removeItem(key)

    this.flush()
    console.info(`✅ 已迁移 ${migratedKeys.length} 个插件存储项从 localStorage 到 IndexedDB`)
  }

  waitForReady(): Promise<void> {
    return this._ready
  }

  get<T = unknown>(key: string): T | null {
    const value = this.cache.get(key)
    return value !== undefined ? (value as T) : null
  }

  set(key: string, value: unknown): void {
    this.cache.set(key, value)
    this.queueWrite(key, value)
  }

  remove(key: string): void {
    this.cache.delete(key)
    this.queueWrite(key, null)
  }

  removeByPrefix(prefix: string): void {
    for (const key of [...this.cache.keys()]) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key)
        this.queueWrite(key, null)
      }
    }
  }

  private queueWrite(key: string, value: unknown | null): void {
    this.writeQueue.set(key, value)
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), 100)
    }
  }

  private flush(): void {
    this.flushTimer = null
    if (this.writeQueue.size === 0)
      return
    const entries = [...this.writeQueue.entries()]
    this.writeQueue.clear()

    if (this.db) {
      // 正常模式：写入 IndexedDB
      try {
        const tx = this.db.transaction(this.storeName, 'readwrite')
        const store = tx.objectStore(this.storeName)
        for (const [key, value] of entries) {
          if (value === null) {
            store.delete(key)
          }
          else {
            // 序列化后再存入，避免 Vue 响应式代理等不可克隆对象导致 structured clone 失败
            store.put(JSON.parse(JSON.stringify(value)), key)
          }
        }
        tx.onerror = () => {
          console.warn('⚠️ 插件存储：IndexedDB 写入失败，降级到 localStorage')
          this.flushToLocalStorage(entries)
        }
      }
      catch {
        console.warn('⚠️ 插件存储：IndexedDB 写入异常，降级到 localStorage')
        this.flushToLocalStorage(entries)
      }
    }
    else {
      // 降级模式：写入 localStorage
      this.flushToLocalStorage(entries)
    }
  }

  /** 降级写入 localStorage（QuotaExceeded 时静默跳过，数据仅保留在内存） */
  private flushToLocalStorage(entries: [string, unknown | null][]): void {
    for (const [key, value] of entries) {
      try {
        if (value === null) {
          localStorage.removeItem(key)
        }
        else {
          localStorage.setItem(key, JSON.stringify(value))
        }
      }
      catch {
        // QuotaExceededError 等 — 静默跳过，数据保留在内存缓存中
      }
    }
  }
}

// 插件 KV 存储单例
const pluginKV = new PluginKVStore()

export const usePluginsStore = defineStore('plugins', () => {
  // ===== 状态 =====
  const installedPlugins = ref<Map<string, InstalledPlugin>>(new Map())
  const activePluginId = ref<string | null>(null)

  // ✨ 插件持久化数据 - 迁移到 IndexedDB（含完整插件包代码，数据量大）
  const pluginsDataStorage = useIndexedDBStorage<any[]>('plugins/installed', [], {
    debounceMs: 500,
  })
  const pluginsDataPersisted = pluginsDataStorage.data

  // ✨ 插件包缓存 - 迁移到 IndexedDB（含完整组件模板/脚本代码）
  const pluginCacheStorage = useIndexedDBStorage<Record<string, PluginPackage>>('plugins/cache', {}, {
    debounceMs: 300,
  })
  const pluginCache = pluginCacheStorage.data

  // 等待存储加载完成后再恢复插件
  const pluginsStorageLoaded = ref(false)
  Promise.all([
    waitForStorage(pluginsDataStorage),
    waitForStorage(pluginCacheStorage),
    pluginKV.waitForReady(),
  ]).then(async () => {
    pluginsStorageLoaded.value = true
    console.debug('✅ 插件存储加载完成（IndexedDB）')
    // 存储就绪后恢复已安装插件
    await loadInstalledPlugins()
  })

  // 自定义消息类型注册表
  const customMessageTypes = ref<Map<string, CustomMessageType>>(new Map())

  // 追踪每个插件注册了哪些消息类型（pluginId -> Set<typeName>）
  const pluginMessageTypeMap = ref<Map<string, Set<string>>>(new Map())

  // 插件注入的系统提示词（key: pluginId, value: prompt content）
  const injectedSystemPrompts = ref<Map<string, string>>(new Map())

  // ===== 钩子系统 =====
  // 存储各种钩子处理器
  const beforeAIReplyHandlers = ref<Map<string, BeforeAIReplyHandler>>(new Map())
  const beforeUserMessageHandlers = ref<Map<string, BeforeUserMessageHandler>>(new Map())
  const afterUserMessageHandlers = ref<Map<string, AfterUserMessageHandler>>(new Map())
  const contactSwitchHandlers = ref<Map<string, ContactSwitchHandler>>(new Map())
  const dynamicPromptGenerators = ref<Map<string, DynamicPromptGenerator>>(new Map())
  const aiActionRegistry = ref<PluginAIActionRegistry>(new Map())

  // ===== 插件错误追踪 =====
  interface PluginError {
    timestamp: number
    phase: string
    message: string
    stack?: string
  }
  const pluginErrors = ref<Map<string, PluginError[]>>(new Map())
  const MAX_ERRORS_PER_PLUGIN = 50

  function addPluginError(pluginId: string, phase: string, error: unknown): void {
    const msg = error instanceof Error ? error.message : String(error)
    const stack = error instanceof Error ? error.stack : undefined
    const errors = pluginErrors.value.get(pluginId) || []
    errors.push({ timestamp: Date.now(), phase, message: msg, stack })
    if (errors.length > MAX_ERRORS_PER_PLUGIN) {
      errors.splice(0, errors.length - MAX_ERRORS_PER_PLUGIN)
    }
    pluginErrors.value.set(pluginId, errors)
  }

  function clearPluginErrors(pluginId: string): void {
    pluginErrors.value.delete(pluginId)
  }

  function getPluginErrors(pluginId: string): PluginError[] {
    return pluginErrors.value.get(pluginId) || []
  }

  // 监听联系人切换并触发插件钩子（只触发已启用插件）
  const contactsStore = useContactsStore()
  watch(
    () => contactsStore.activeContactId,
    async (toContactId, fromContactId) => {
      if (!toContactId || toContactId === fromContactId) {
        return
      }
      for (const [pluginId, handler] of contactSwitchHandlers.value.entries()) {
        const installed = installedPlugins.value.get(pluginId)
        if (!installed?.enabled) {
          continue
        }
        try {
          await handler(fromContactId ?? null, toContactId)
        }
        catch (error) {
          console.error(`❌ 插件 ${pluginId} 联系人切换钩子执行失败:`, error)
          addPluginError(pluginId, '联系人切换钩子', error)
        }
      }
    },
  )

  // ===== 计算属性 =====
  const enabledPlugins = computed(() => {
    return Array.from(installedPlugins.value.values())
      .filter(p => p.enabled)
  })

  const pluginsByLocation = computed(() => {
    return (location: PluginLocation) => {
      return enabledPlugins.value.filter(p =>
        p.plugin.metadata.location.includes(location),
      )
    }
  })

  const availableAIActions = computed<RegisteredPluginAIAction[]>(() => {
    const actions: RegisteredPluginAIAction[] = []
    for (const installed of enabledPlugins.value) {
      const pluginId = installed.plugin.metadata.id
      const pluginActions = aiActionRegistry.value.get(pluginId)
      if (!pluginActions)
        continue

      for (const action of pluginActions.values()) {
        actions.push({
          pluginId,
          pluginName: installed.plugin.metadata.name,
          name: action.name,
          description: action.description,
          parameters: action.parameters,
        })
      }
    }
    return actions
  })

  // ===== 辅助函数：构建联系人信息 =====
  function normalizeAIActionParams(params: unknown): Record<string, unknown> {
    if (params && typeof params === 'object' && !Array.isArray(params))
      return params as Record<string, unknown>
    return {}
  }

  function registerPluginAIAction(pluginId: string, action: PluginAIActionDefinition): void {
    const installed = installedPlugins.value.get(pluginId)
    if (!installed || !hasPluginPermission(installed.plugin, 'ai')) {
      const error = new Error(`Plugin ${pluginId} requires ai permission to register AI actions`)
      addPluginError(pluginId, 'AI动作注册', error)
      throw error
    }

    const actionName = action.name?.trim()
    const description = action.description?.trim()
    if (!actionName) {
      throw new Error('AI action name is required')
    }
    if (!/^[\w.-]{1,80}$/.test(actionName)) {
      throw new Error('AI action name may only contain letters, numbers, underscores, dots, and hyphens')
    }
    if (!description) {
      throw new Error('AI action description is required')
    }
    if (typeof action.execute !== 'function') {
      throw new TypeError('AI action execute must be a function')
    }

    const registry = new Map(aiActionRegistry.value)
    const pluginActions = new Map(registry.get(pluginId) || [])
    pluginActions.set(actionName, markRaw({
      ...action,
      name: actionName,
      description,
    }))
    registry.set(pluginId, pluginActions)
    aiActionRegistry.value = registry
    console.info(`✅ 插件 ${pluginId} 注册了 AI 动作: ${actionName}`)
  }

  function unregisterPluginAIAction(pluginId: string, actionName: string): void {
    const normalizedName = actionName.trim()
    if (!normalizedName)
      return

    const currentActions = aiActionRegistry.value.get(pluginId)
    if (!currentActions?.has(normalizedName))
      return

    const registry = new Map(aiActionRegistry.value)
    const pluginActions = new Map(currentActions)
    pluginActions.delete(normalizedName)
    if (pluginActions.size > 0) {
      registry.set(pluginId, pluginActions)
    }
    else {
      registry.delete(pluginId)
    }
    aiActionRegistry.value = registry
    console.info(`🧹 插件 ${pluginId} 注销了 AI 动作: ${normalizedName}`)
  }

  async function executeAIAction(
    pluginId: string,
    actionName: string,
    params: unknown = {},
    meta?: { userInput?: string },
  ): Promise<unknown> {
    const installed = installedPlugins.value.get(pluginId)
    if (!installed?.enabled) {
      const error = new Error(`Plugin is not enabled: ${pluginId}`)
      addPluginError(pluginId, 'AI动作查找', error)
      throw error
    }

    if (!hasPluginPermission(installed.plugin, 'ai')) {
      const error = new Error(`Plugin ${pluginId} requires ai permission to execute AI actions`)
      addPluginError(pluginId, 'AI动作权限', error)
      throw error
    }

    const action = aiActionRegistry.value.get(pluginId)?.get(actionName)
    if (!action) {
      const error = new Error(`AI action not found: ${pluginId}.${actionName}`)
      addPluginError(pluginId, 'AI动作查找', error)
      throw error
    }

    try {
      return await action.execute(normalizeAIActionParams(params), {
        contactId: contactsStore.activeContactId,
        userInput: meta?.userInput,
      })
    }
    catch (error) {
      addPluginError(pluginId, `AI动作:${actionName}`, error)
      throw error
    }
  }

  // ===== 辅助函数：构建联系人信息 =====
  function buildContactInfo(contact: any): ContactInfo {
    const airiCardStore = useAiriCardStore()
    const card = contact.cardId ? airiCardStore.getCard(contact.cardId) : null
    const persona = card
      ? [card.description, card.personality, card.scenario]
          .filter(Boolean)
          .join('\n')
      : ''

    return {
      id: contact.id,
      name: contact.name,
      avatar: contact.avatar,
      cardId: contact.cardId,
      persona,
    }
  }

  // ===== 创建插件上下文 =====
  function notifyPluginMessageSent(pluginId: string, contactId: string): void {
    // 通知由宿主创建短生命周期通道，避免插件弹窗卸载后继续持有已关闭的 BroadcastChannel。
    try {
      const channel = new BroadcastChannel('meowchat-plugin-messages')
      channel.postMessage({
        type: 'plugin_message_sent',
        data: { pluginId, contactId, timestamp: Date.now() },
      })
      channel.close()
    }
    catch (error) {
      console.warn('插件消息通知发送失败:', error)
    }
  }

  function createPluginContext(pluginId: string): PluginContext {
    const contactsStore = useContactsStore()
    const chatStore = useChatStore()

    return {
      pluginId,

      // 基础消息API
      sendMessage: async (message: string) => {
        const contactId = contactsStore.activeContactId
        if (!contactId) {
          throw new Error('No active contact')
        }
        await chatStore.sendUserMessage(message)
        notifyPluginMessageSent(pluginId, contactId)
      },

      sendStructuredMessage: async (data: { type: string, display: string, data: any, contactId?: string }) => {
        const targetContactId = data.contactId || contactsStore.activeContactId
        if (!targetContactId) {
          throw new Error('No active contact')
        }
        // 发送格式: [TYPE]jsonData[/TYPE] + 显示文本
        const messageText = `[${data.type.toUpperCase()}]${JSON.stringify(data.data)}[/${data.type.toUpperCase()}] ${data.display}`

        if (data.contactId && data.contactId !== contactsStore.activeContactId) {
          // 校验联系人是否存在
          if (!contactsStore.contacts[data.contactId]) {
            throw new Error(`Contact not found: ${data.contactId}`)
          }
          // 指定联系人：直接插入消息到目标对话
          chatStore.pushMessageToContact(targetContactId, {
            role: 'user',
            content: messageText,
            timestamp: Date.now(),
          } as any)
        }
        else {
          // 默认行为：发送到当前活跃联系人
          await chatStore.sendUserMessage(messageText)
        }

        // 触发防抖机制：由宿主统一广播，插件无需管理通信通道生命周期。
        notifyPluginMessageSent(pluginId, targetContactId)
      },

      // 消息类型注册
      registerMessageType: (config: CustomMessageType) => {
        // 替换整个 Map 引用以确保 Vue 响应式系统可靠地检测到变化
        const newMap = new Map(customMessageTypes.value)
        newMap.set(config.type, markRaw(config))
        customMessageTypes.value = newMap

        // 追踪该插件注册了哪些消息类型
        if (!pluginMessageTypeMap.value.has(pluginId)) {
          pluginMessageTypeMap.value.set(pluginId, new Set())
        }
        pluginMessageTypeMap.value.get(pluginId)!.add(config.type)

        console.info(`✅ 插件 ${pluginId} 注册了自定义消息类型: ${config.type}，当前总数: ${newMap.size}`)
      },

      registerAIAction: (action: PluginAIActionDefinition) => {
        registerPluginAIAction(pluginId, action)
      },

      unregisterAIAction: (name: string) => {
        unregisterPluginAIAction(pluginId, name)
      },

      // 关闭插件（用于聊天插件）
      closePlugin: () => {
        activePluginId.value = null
      },

      getCurrentContactId: () => {
        return contactsStore.activeContactId
      },

      showToast: (message: string, type = 'info' as const) => {
        if (type === 'error') {
          showError(message)
        }
        else if (type === 'success') {
          showSuccess(message)
        }
        else {
          showInfo(message)
        }
      },

      // 配置管理
      getConfig: <T = any>(key: string): T | undefined => {
        const configKey = `${CONFIG_STORAGE_PREFIX}${pluginId}/${key}`
        const value = pluginKV.get(configKey)
        return value !== null ? value as T : undefined
      },

      setConfig: (key: string, value: any) => {
        const configKey = `${CONFIG_STORAGE_PREFIX}${pluginId}/${key}`
        pluginKV.set(configKey, value)
      },

      // 插件私有存储
      storage: {
        get: <T = any>(key: string): T | null => {
          const storageKey = `${PLUGIN_DATA_PREFIX}${pluginId}/${key}`
          return pluginKV.get<T>(storageKey)
        },

        set: (key: string, value: any) => {
          const storageKey = `${PLUGIN_DATA_PREFIX}${pluginId}/${key}`
          pluginKV.set(storageKey, value)
        },

        remove: (key: string) => {
          const storageKey = `${PLUGIN_DATA_PREFIX}${pluginId}/${key}`
          pluginKV.remove(storageKey)
        },
      },

      // 全局共享存储
      globalStorage: {
        get: <T = any>(key: string): T | null => {
          const storageKey = `${GLOBAL_STORAGE_PREFIX}${key}`
          return pluginKV.get<T>(storageKey)
        },

        set: (key: string, value: any) => {
          const storageKey = `${GLOBAL_STORAGE_PREFIX}${key}`
          pluginKV.set(storageKey, value)
        },

        remove: (key: string) => {
          const storageKey = `${GLOBAL_STORAGE_PREFIX}${key}`
          pluginKV.remove(storageKey)
        },
      },

      // 联系人API
      contacts: {
        getAll: (): ContactInfo[] => {
          // 过滤掉群聊，只返回单人联系人
          return Object.values(contactsStore.contacts)
            .filter((contact: any) => !contact.isGroup)
            .map(buildContactInfo)
        },

        getById: (id: string): ContactInfo | null => {
          const contact = contactsStore.contacts[id]
          return contact ? buildContactInfo(contact) : null
        },

        getCurrent: (): ContactInfo | null => {
          const contactId = contactsStore.activeContactId
          if (!contactId)
            return null
          const contact = contactsStore.contacts[contactId]
          return contact ? buildContactInfo(contact) : null
        },
      },

      // 用户人格API
      userPersona: {
        getCurrent: () => {
          const userPersonaStore = useUserPersonaStore()
          const activeContact = contactsStore.activeContactId
            ? contactsStore.contacts[contactsStore.activeContactId]
            : null
          const persona = userPersonaStore.resolvePersonaForContact(activeContact)
          if (!persona)
            return null
          return {
            id: persona.id,
            name: persona.name,
            avatar: persona.avatar,
            birthday: persona.birthday,
            gender: persona.gender,
            persona: persona.persona,
          }
        },
        getAll: () => {
          const userPersonaStore = useUserPersonaStore()
          return userPersonaStore.personaPresets.map((p: any) => ({
            id: p.id,
            name: p.name,
            avatar: p.avatar,
            birthday: p.birthday,
            gender: p.gender,
            persona: p.persona,
          }))
        },
      },

      // 世界书API
      worldbooks: {
        getAll: () => {
          const worldBookStore = useWorldBookStore()
          return worldBookStore.worldBooks.map((wb: any) => ({
            id: wb.id,
            name: wb.name,
            content: wb.content,
            triggerType: wb.triggerType,
            keywords: wb.keywords,
          }))
        },
        getById: (id: string) => {
          const worldBookStore = useWorldBookStore()
          const wb = worldBookStore.getWorldBook(id)
          if (!wb)
            return null
          return {
            id: wb.id,
            name: wb.name,
            content: wb.content,
            triggerType: wb.triggerType,
            keywords: wb.keywords,
          }
        },
        getEnabled: () => {
          const worldBookStore = useWorldBookStore()
          const contactId = contactsStore.activeContactId
          if (!contactId)
            return []
          const contact = contactsStore.contacts[contactId]
          if (!contact)
            return []
          const enabledIds = (contact as any).worldBookIds || []
          if (enabledIds.length === 0)
            return []
          const enabledSet = new Set(enabledIds)
          return worldBookStore.worldBooks
            .filter((wb: any) => enabledSet.has(wb.id))
            .map((wb: any) => ({
              id: wb.id,
              name: wb.name,
              content: wb.content,
              triggerType: wb.triggerType,
              keywords: wb.keywords,
            }))
        },
      },

      // 贴纸表情包API
      stickers: (() => {
        const getActivePackIds = () => {
          const stickersStore = useStickersStore()
          const userPersonaStore = useUserPersonaStore()
          const activeContact = contactsStore.activeContactId
            ? contactsStore.contacts[contactsStore.activeContactId]
            : null
          const persona = userPersonaStore.resolvePersonaForContact(activeContact)
          return { stickersStore, packIds: persona?.stickerPackIds || [] }
        }
        return {
          getPacks: () => {
            const { stickersStore, packIds } = getActivePackIds()
            return packIds
              .map(id => stickersStore.getPackById(id))
              .filter((p): p is NonNullable<typeof p> => !!p)
              .map(pack => ({
                id: pack.id,
                name: pack.name,
                stickers: pack.stickers.map(s => ({ id: s.id, name: s.name, url: s.url })),
              }))
          },
          getAll: () => {
            const { stickersStore, packIds } = getActivePackIds()
            return stickersStore.getStickersForPacks(packIds).map(s => ({ id: s.id, name: s.name, url: s.url }))
          },
        }
      })(),

      // 系统提示词注入
      injectSystemPrompt: (content: string) => {
        injectedSystemPrompts.value.set(pluginId, content)
        console.info(`✅ 插件 ${pluginId} 注入了系统提示词`)
      },

      // ===== 钩子系统 =====

      // 注册 AI 消息处理钩子
      onBeforeAIReply: (handler: BeforeAIReplyHandler) => {
        const wrapped: BeforeAIReplyHandler = async (message, metadata) => {
          try {
            return await handler(message, metadata)
          }
          catch (error) {
            addPluginError(pluginId, 'AI回复钩子', error)
            throw error
          }
        }
        beforeAIReplyHandlers.value.set(pluginId, wrapped)
        console.info(`✅ 插件 ${pluginId} 注册了 AI 消息处理钩子`)
      },

      // 注册用户消息处理钩子（发送前）
      onBeforeUserMessage: (handler: BeforeUserMessageHandler) => {
        const wrapped: BeforeUserMessageHandler = async (message, metadata) => {
          try {
            return await handler(message, metadata)
          }
          catch (error) {
            addPluginError(pluginId, '用户消息钩子', error)
            throw error
          }
        }
        beforeUserMessageHandlers.value.set(pluginId, wrapped)
        console.info(`✅ 插件 ${pluginId} 注册了用户消息处理钩子（发送前）`)
      },

      // 注册用户消息处理钩子（发送后）
      onAfterUserMessage: (handler: AfterUserMessageHandler) => {
        const wrapped: AfterUserMessageHandler = async (message, metadata) => {
          try {
            await handler(message, metadata)
          }
          catch (error) {
            addPluginError(pluginId, '消息发送后钩子', error)
            throw error
          }
        }
        afterUserMessageHandlers.value.set(pluginId, wrapped)
        console.info(`✅ 插件 ${pluginId} 注册了用户消息处理钩子（发送后）`)
      },

      // 注册联系人切换钩子
      onContactSwitch: (handler: ContactSwitchHandler) => {
        const wrapped: ContactSwitchHandler = async (fromContactId, toContactId) => {
          try {
            await handler(fromContactId, toContactId)
          }
          catch (error) {
            addPluginError(pluginId, '联系人切换钩子', error)
            throw error
          }
        }
        contactSwitchHandlers.value.set(pluginId, wrapped)
        console.info(`✅ 插件 ${pluginId} 注册了联系人切换钩子`)
      },

      // 动态提示词生成器
      injectDynamicPrompt: (generator: DynamicPromptGenerator) => {
        const wrapped: DynamicPromptGenerator = (context) => {
          try {
            return generator(context)
          }
          catch (error) {
            addPluginError(pluginId, '动态提示词', error)
            return null
          }
        }
        dynamicPromptGenerators.value.set(pluginId, wrapped)
        console.info(`✅ 插件 ${pluginId} 注册了动态提示词生成器`)
      },

      // ===== 高级API =====

      // 获取 AI 配置（使用 MeowChat 的配置）
      getAIConfig: async () => {
        const consciousnessStore = useConsciousnessStore()
        const providersStore = useProvidersStore()

        // activeProvider 和 activeModel 是 useLocalStorage 返回的 ref
        // 直接访问即可，Pinia 会自动解包
        const providerValue = String(consciousnessStore.activeProvider || '')
        const modelValue = String(consciousnessStore.activeModel || '')

        console.info('[getAIConfig] Provider:', providerValue, 'Model:', modelValue)

        if (!providerValue) {
          console.warn('[getAIConfig] No active provider configured')
          return null
        }

        const config = providersStore.getProviderConfig(providerValue)
        if (!config) {
          console.warn('[getAIConfig] No config found for provider:', providerValue)
          return null
        }

        // 返回 OpenAI 兼容格式的配置
        // 安全策略：apiKey 对插件不可见，插件想调 AI 必须走 context.callAI（由宿主代发请求）
        const result = {
          apiUrl: (config.baseUrl as string) || '',
          apiKey: '',
          model: modelValue,
          provider: providerValue,
        }

        console.info('[getAIConfig] Returning config:', {
          provider: result.provider,
          model: result.model,
          hasApiKey: !!result.apiKey,
          apiUrl: result.apiUrl,
        })

        return result
      },

      // 调用 AI（支持流式和非流式，支持多模态内容，自动处理 SSE 解析）
      callAI: async (options: {
        messages: Array<{ role: 'user' | 'assistant' | 'system', content: string | Array<{ type: string, [key: string]: any }> }>
        stream?: boolean
        onChunk?: (text: string) => void
        temperature?: number
      }): Promise<string> => {
        // 每插件限流：防止恶意插件刷爆用户 AI 额度
        callAIRateCheck(pluginId)

        const consciousnessStore = useConsciousnessStore()
        const providersStore = useProvidersStore()

        const providerValue = String(consciousnessStore.activeProvider || '')
        const modelValue = String(consciousnessStore.activeModel || '')

        if (!providerValue) {
          throw new Error('[callAI] No active provider configured')
        }

        const config = providersStore.getProviderConfig(providerValue)
        if (!config) {
          throw new Error(`[callAI] No config found for provider: ${providerValue}`)
        }

        const metadata = providersStore.getProviderMetadata(providerValue)
        if (!metadata?.createProvider) {
          throw new Error(`[callAI] No provider metadata found for: ${providerValue}`)
        }

        const chatProvider = await metadata.createProvider(config) as any
        if (!chatProvider?.chat) {
          throw new Error(`[callAI] Provider does not support chat: ${providerValue}`)
        }

        const chatConfig = chatProvider.chat(modelValue)

        // Claude 模型：注入 prompt caching 支持
        const claudeModel = isClaudeModel(modelValue)
        const claudeHeaders = claudeModel ? getClaudeHeaders() : undefined

        const processedMessages = claudeModel
          ? (() => {
              const msgs = options.messages.map(m => ({ ...m }))
              injectCacheControl(msgs)
              return msgs
            })()
          : options.messages

        console.info(`[callAI] Plugin ${pluginId} calling AI: provider=${providerValue}, model=${modelValue}, stream=${!!options.stream}, messages=${options.messages.length}`)

        if (options.stream) {
          // 流式模式：使用 streamText，自动处理 SSE 解析
          const result = await streamText({
            ...chatConfig,
            messages: processedMessages,
            headers: claudeHeaders,
            ...(options.temperature != null ? { temperature: options.temperature } : {}),
          })

          let fullText = ''
          for await (const chunk of readableStreamToAsyncIterator(result.textStream)) {
            fullText += chunk
            options.onChunk?.(chunk)
          }

          console.info(`[callAI] Plugin ${pluginId} stream completed, length=${fullText.length}`)
          return fullText
        }
        else {
          // 非流式模式：使用 generateText，直接返回完整文本
          const result = await generateText({
            ...chatConfig,
            messages: processedMessages,
            headers: claudeHeaders,
            ...(options.temperature != null ? { temperature: options.temperature } : {}),
          })

          const text = result.text || ''
          console.info(`[callAI] Plugin ${pluginId} generate completed, length=${text.length}`)
          return text
        }
      },

      // 访问聊天历史
      getChatHistory: (options: any = {}): Message[] => {
        // 支持通过 options.contactId 指定联系人，否则使用当前激活的联系人
        const targetContactId = options.contactId || contactsStore.activeContactId
        if (!targetContactId) {
          return []
        }

        // 使用 chatStore 提供的正式 API 获取指定联系人的消息
        const contactMessages = chatStore.getMessagesForContact(targetContactId)
        // 仅保留标准聊天角色，避免将 error 等内部消息暴露给插件上下文
        let filtered = contactMessages.filter((m: any) =>
          m.role === 'user' || m.role === 'assistant' || m.role === 'system',
        ) as Message[]

        if (options.filterByRole) {
          filtered = filtered.filter((m: any) => m.role === options.filterByRole)
        }

        if (options.limit) {
          filtered = filtered.slice(-options.limit)
        }

        return filtered.map((message: any) => {
          const simulatedImages = message.simulatedImages
          if (!Array.isArray(simulatedImages) || simulatedImages.length === 0)
            return message

          const imageText = simulatedImages.map((image: any, index: number) => {
            const description = typeof image?.description === 'string' ? image.description.trim() : ''
            return description ? `图片${index + 1}：${description}` : ''
          }).filter(Boolean).join('\n')
          return imageText
            ? { ...message, content: `[发送了${simulatedImages.length > 1 ? `${simulatedImages.length}张` : '一张'}模拟图片]\n${imageText}` }
            : message
        }) as Message[]
      },

      // 更新消息
      updateMessage: async (messageId: string, newContent: string) => {
        const contactId = contactsStore.activeContactId
        if (!contactId) {
          throw new Error('No active contact')
        }

        // messageId 通常是 timestamp 的字符串形式
        const timestamp = Number(messageId)
        if (Number.isNaN(timestamp)) {
          throw new TypeError('Invalid messageId')
        }

        // 调用 chatStore 的 editMessage 方法
        if (typeof (chatStore as any).editMessage === 'function') {
          (chatStore as any).editMessage(timestamp, newContent)
          console.info(`✅ 插件 ${pluginId} 更新了消息 ${messageId}`)
        }
        else {
          console.warn('chatStore.editMessage 方法未找到')
        }
      },

      // ===== 工具函数库 =====
      utils: {
        generateId(prefix?: string): string {
          const arr = new Uint8Array(6)
          crypto.getRandomValues(arr)
          const rand = Array.from(arr, b => b.toString(36).padStart(2, '0')).join('')
          return prefix ? `${prefix}_${rand}` : rand
        },
        uid(): string {
          return `uid-${Date.now().toString(36)}-${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}`
        },
        deepClone<T>(obj: T): T {
          return structuredClone(obj)
        },
        throttle(fn: (...args: any[]) => any, delay: number) {
          let last = 0
          return function (this: any, ...args: any[]) {
            const now = Date.now()
            if (now - last >= delay) {
              last = now
              return fn.apply(this, args)
            }
          }
        },
        debounce(fn: (...args: any[]) => any, delay: number) {
          let timer: ReturnType<typeof setTimeout> | null = null
          return function (this: any, ...args: any[]) {
            if (timer)
              clearTimeout(timer)
            timer = setTimeout(() => fn.apply(this, args), delay)
          }
        },
        formatDate(ts: number | string): string {
          const d = new Date(typeof ts === 'string' ? Number(ts) || ts : ts)
          if (Number.isNaN(d.getTime()))
            return 'Invalid Date'
          const pad = (n: number) => String(n).padStart(2, '0')
          return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
        },
        formatBytes(bytes: number): string {
          if (bytes <= 0)
            return '0 B'
          const k = 1024
          const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
          const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
          return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
        },
        escapeHtml(str: string): string {
          return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
        },
        pick<T extends object>(obj: T, keys: string[]): Partial<T> {
          const r: any = {}
          for (const k of keys) {
            if (k in obj)
              r[k] = (obj as any)[k]
          }
          return r
        },
        omit<T extends object>(obj: T, keys: string[]): Partial<T> {
          const r: any = {}
          for (const k of Object.keys(obj)) {
            if (!keys.includes(k))
              r[k] = (obj as any)[k]
          }
          return r
        },
        sleep(ms: number): Promise<void> {
          return new Promise(resolve => setTimeout(resolve, ms))
        },
      },

      // ===== 数据集合管理 =====
      db(collectionName: string) {
        const storageKey = `${GLOBAL_STORAGE_PREFIX}fwk-db-${pluginId}-${collectionName}`
        let cached: Record<string, any>[] | null = null
        const load = () => {
          if (!cached)
            cached = pluginKV.get<Record<string, any>[]>(storageKey) || []
          return cached
        }
        const save = (items: Record<string, any>[]) => {
          cached = items
          pluginKV.set(storageKey, items)
        }
        return {
          insert(data: Record<string, any>): Record<string, any> & { _id: string, _createdAt: string } {
            const items = load()
            const item = {
              ...data,
              _id: crypto.randomUUID(),
              _createdAt: new Date().toISOString(),
            }
            items.push(item)
            save(items)
            return item
          },
          update(id: string, updates: Record<string, any>) {
            const items = load()
            const idx = items.findIndex(i => i._id === id)
            if (idx === -1)
              return null
            items[idx] = { ...items[idx], ...updates, _updatedAt: new Date().toISOString() }
            save(items)
            return items[idx]
          },
          remove(id: string) {
            const items = load()
            const idx = items.findIndex(i => i._id === id)
            if (idx === -1)
              return false
            items.splice(idx, 1)
            save(items)
            return true
          },
          getAll: () => load(),
          find: (predicate: (item: Record<string, any>) => boolean) => load().filter(predicate),
          findOne: (predicate: (item: Record<string, any>) => boolean) => load().find(predicate) || null,
          getById: (id: string) => load().find(i => i._id === id) || null,
          count: () => load().length,
          clear() { save([]) },
        }
      },

      // ===== 带 TTL 的缓存 =====
      cache: (() => {
        // 提取过期检查逻辑，消除 get/has 重复代码
        const cacheKey = (key: string) => `${GLOBAL_STORAGE_PREFIX}fwk-cache-${pluginId}-${key}`
        const resolve = <T>(key: string): { v: T, exp?: number } | null => {
          const raw = pluginKV.get<{ v: T, exp?: number }>(cacheKey(key))
          if (!raw)
            return null
          if (raw.exp && Date.now() > raw.exp) {
            pluginKV.remove(cacheKey(key))
            return null
          }
          return raw
        }
        return {
          get<T = any>(key: string): T | null {
            return (resolve<T>(key)?.v) ?? null
          },
          set(key: string, value: any, ttlMs?: number) {
            const entry: { v: any, exp?: number } = { v: value }
            if (ttlMs)
              entry.exp = Date.now() + ttlMs
            pluginKV.set(cacheKey(key), entry)
          },
          remove(key: string) {
            pluginKV.remove(cacheKey(key))
          },
          has(key: string): boolean {
            return resolve(key) !== null
          },
        }
      })(),

      // ===== 第三方 API 代理 =====
      fetch: async (url: string, options?: RequestInit): Promise<Response> => {
        // 敏感域名黑名单：AI provider 官方地址 + meowchat 自家服务
        // 避免恶意插件拿到 key 后直接打 AI 官方接口消耗额度，或中转绕过 callAI 限流
        if (isBlockedFetchTarget(url)) {
          throw new Error(`[plugin.fetch] 目标地址被安全策略拦截：请通过 context.callAI 调用 AI，或改用非敏感域名`)
        }
        const proxyUrl = `/api/proxy?url=${encodeURIComponent(url)}`
        return globalThis.fetch(proxyUrl, {
          method: options?.method || 'GET',
          headers: options?.headers,
          body: options?.body,
          signal: options?.signal,
        })
      },

      // ===== UI 模板工厂 =====
      ui: (() => {
        // 安全转义，防止 XSS
        const esc = (s: string) => String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')
        // CSS 颜色值白名单校验（只允许 #hex 或 rgb/rgba/hsl 格式）
        const safeColor = (c?: string, fallback = '#3b82f6') =>
          c && /^(?:#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))$/.test(c) ? c : fallback
        return {
          card(opts: { bgColor?: string, bgColorEnd?: string, icon?: string, subtitle?: string, content: string, footer?: string }): string {
            const bg = safeColor(opts.bgColor)
            const bgEnd = safeColor(opts.bgColorEnd, '#2563eb')
            return `<div style="border-radius:1rem;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);max-width:320px;">`
              + `<div style="background:linear-gradient(135deg,${bg},${bgEnd});padding:1.25rem">${
                opts.icon || opts.subtitle
                  ? `<div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.5rem">${
                    opts.icon ? `<span style="font-size:1.1rem;color:white">${esc(opts.icon)}</span>` : ''
                  }${opts.subtitle ? `<span style="color:rgba(255,255,255,0.9);font-size:0.85rem">${esc(opts.subtitle)}</span>` : ''
                  }</div>`
                  : ''
              }<div style="color:white;font-size:1.1rem;line-height:1.4">${esc(opts.content)}</div>`
              + `</div>`
              + `<div style="padding:0.6rem 1.25rem;background:#f8fafc">`
              + `<span style="font-size:0.75rem;color:#64748b">${esc(opts.footer || 'MeowChat')}</span>`
              + `</div></div>`
          },
          listItem(opts: { title: string, desc?: string, badge?: string }): string {
            return `<div style="display:flex;align-items:center;gap:0.75rem;padding:0.75rem;border-radius:0.75rem;background:white;border:1px solid #e5e7eb;margin-bottom:0.5rem">`
              + `<div style="flex:1;min-width:0">`
              + `<div style="font-weight:600;color:#1f2937;font-size:0.875rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(opts.title)}</div>${
                opts.desc ? `<div style="color:#6b7280;font-size:0.75rem;margin-top:0.25rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(opts.desc)}</div>` : ''
              }</div>${
                opts.badge ? `<div style="flex-shrink:0">${opts.badge}</div>` : ''
              }</div>`
          },
          badge(text: string, color?: string): string {
            return `<span style="display:inline-block;padding:0.15rem 0.5rem;border-radius:9999px;font-size:0.7rem;color:white;background:${safeColor(color)}">${esc(text)}</span>`
          },
        }
      })(),
    }
  }

  // ===== 安装插件 =====
  async function installPlugin(plugin: Plugin, pluginPackage: PluginPackage, source: InstalledPlugin['source'] = 'local', sourceUrl?: string, options?: { silent?: boolean, replaceExisting?: boolean, transient?: boolean }): Promise<void> {
    try {
      validatePlugin(plugin)

      const normalizedPackage = normalizePluginPackage(pluginPackage)
      const pluginId = plugin.metadata.id
      const existingPlugin = installedPlugins.value.get(pluginId)
      const previousCache = pluginCache.value[pluginId]
      const previousStyle = getPluginStyleContent(pluginId)

      if (existingPlugin) {
        if (source !== 'builtin' && existingPlugin.source !== 'builtin' && source !== 'url' && !options?.replaceExisting) {
          throw new Error(`Plugin ${pluginId} is already installed`)
        }

        console.info(`🔄 更新插件: ${plugin.metadata.name}`)

        const restoreExistingPluginState = async (): Promise<void> => {
          cleanupPluginRuntime(pluginId)
          installedPlugins.value.set(pluginId, existingPlugin)
          if (previousCache) {
            pluginCache.value = { ...pluginCache.value, [pluginId]: previousCache }
            if (existingPlugin.enabled) {
              if (previousCache.vueStyle !== undefined) {
                injectPluginStyle(previousCache)
              }
              else {
                restorePluginStyle(pluginId, previousStyle)
              }
            }
          }
          else {
            const { [pluginId]: _removed, ...restCache } = pluginCache.value
            pluginCache.value = restCache
            if (existingPlugin.enabled)
              restorePluginStyle(pluginId, previousStyle)
          }
          if (existingPlugin.enabled) {
            try {
              const oldContext = createPluginContext(pluginId)
              await initializePlugin(existingPlugin.plugin, oldContext)
            }
            catch (restoreError) {
              console.error(`❌ 插件 ${existingPlugin.plugin.metadata.name} 恢复运行时失败:`, restoreError)
              addPluginError(pluginId, '恢复', restoreError)
            }
          }
        }

        try {
          if (existingPlugin.enabled && existingPlugin.plugin.onUnload) {
            const oldContext = createPluginContext(pluginId)
            await existingPlugin.plugin.onUnload(oldContext)
          }
          cleanupPluginRuntime(pluginId)

          const markedPlugin = {
            ...plugin,
            component: markRaw(plugin.component),
          }
          const updatedPlugin: InstalledPlugin = {
            ...existingPlugin,
            plugin: markedPlugin,
            source,
            sourceUrl: sourceUrl ?? existingPlugin.sourceUrl,
            updatedAt: Date.now(),
            transient: options?.transient,
          }
          installedPlugins.value.set(pluginId, updatedPlugin)

          if (existingPlugin.enabled) {
            const context = createPluginContext(pluginId)
            await initializePlugin(plugin, context)
            injectPluginStyle(normalizedPackage)
          }
          if (!options?.transient)
            pluginCache.value = { ...pluginCache.value, [pluginId]: normalizedPackage }
          saveInstalledPlugins()
        }
        catch (error) {
          await restoreExistingPluginState()
          throw error
        }

        if (!options?.silent) {
          showSuccess(`插件已更新：${plugin.metadata.name}`)
        }
        return
      }

      const markedPlugin = {
        ...plugin,
        component: markRaw(plugin.component),
      }

      const installedPlugin: InstalledPlugin = {
        plugin: markedPlugin,
        enabled: true,
        installedAt: Date.now(),
        updatedAt: Date.now(),
        source,
        sourceUrl,
        transient: options?.transient,
      }

      installedPlugins.value.set(pluginId, installedPlugin)

      try {
        const context = createPluginContext(pluginId)
        await initializePlugin(plugin, context)
        injectPluginStyle(normalizedPackage)
        if (!options?.transient)
          pluginCache.value = { ...pluginCache.value, [pluginId]: normalizedPackage }
        saveInstalledPlugins()
      }
      catch (error) {
        cleanupPluginRuntime(pluginId)
        installedPlugins.value.delete(pluginId)
        const { [pluginId]: _removed, ...restCache } = pluginCache.value
        pluginCache.value = restCache
        throw error
      }

      console.info(`✅ Plugin installed: ${plugin.metadata.name}`)
      if (!options?.silent) {
        showSuccess(`插件安装成功：${plugin.metadata.name}`)
      }
    }
    catch (error) {
      console.error('Failed to install plugin:', error)
      if (!options?.silent) {
        showError(`插件安装失败：${error instanceof Error ? error.message : '未知错误'}`)
      }
      throw error
    }
  }

  // ===== 卸载插件 =====
  async function uninstallPlugin(pluginId: string, options?: { preserveConfig?: boolean, silent?: boolean }): Promise<void> {
    try {
      const installedPlugin = installedPlugins.value.get(pluginId)
      if (!installedPlugin) {
        return
      }

      if (installedPlugin.plugin.onUnload) {
        const context = createPluginContext(pluginId)
        await installedPlugin.plugin.onUnload(context)
      }

      installedPlugins.value.delete(pluginId)
      cleanupPluginRuntime(pluginId)

      if (!options?.preserveConfig) {
        cleanupPluginConfig(pluginId)
      }

      const { [pluginId]: _removed, ...restCache } = pluginCache.value
      pluginCache.value = restCache
      saveInstalledPlugins()

      console.info(`✅ Plugin uninstalled: ${installedPlugin.plugin.metadata.name}`)
      if (!options?.silent) {
        showSuccess(`插件已卸载：${installedPlugin.plugin.metadata.name}`)
      }
    }
    catch (error) {
      console.error('Failed to uninstall plugin:', error)
      if (!options?.silent) {
        showError(`插件卸载失败：${error instanceof Error ? error.message : '未知错误'}`)
      }
      throw error
    }
  }
  // ===== 启用/禁用插件 =====
  async function togglePlugin(pluginId: string): Promise<void> {
    const installedPlugin = installedPlugins.value.get(pluginId)
    if (!installedPlugin) {
      return
    }

    const nextEnabled = !installedPlugin.enabled
    installedPlugin.enabled = nextEnabled
    installedPlugin.updatedAt = Date.now()

    if (nextEnabled) {
      try {
        if (installedPlugin.plugin.onLoad) {
          const context = createPluginContext(pluginId)
          await installedPlugin.plugin.onLoad(context)
        }
        const cachedPackage = pluginCache.value[pluginId]
        if (cachedPackage)
          injectPluginStyle(cachedPackage)
      }
      catch (error) {
        installedPlugin.enabled = false
        cleanupPluginRuntime(pluginId)
        addPluginError(pluginId, '启用', error)
        showError(`插件启用失败：${error instanceof Error ? error.message : '未知错误'}`)
        saveInstalledPlugins()
        return
      }
    }
    else {
      if (activePluginId.value === pluginId)
        activePluginId.value = null

      if (installedPlugin.plugin.onUnload) {
        try {
          const context = createPluginContext(pluginId)
          await installedPlugin.plugin.onUnload(context)
        }
        catch (error) {
          console.error(`❌ 插件 ${installedPlugin.plugin.metadata.name} onUnload 执行失败:`, error)
          addPluginError(pluginId, '禁用', error)
        }
      }
      cleanupPluginRuntime(pluginId)
    }

    saveInstalledPlugins()

    const status = installedPlugin.enabled ? '启用' : '禁用'
    showInfo(`插件已${status}：${installedPlugin.plugin.metadata.name}`)
  }

  // ===== 激活插件 =====
  async function activatePlugin(pluginId: string): Promise<void> {
    const installedPlugin = installedPlugins.value.get(pluginId)
    if (!installedPlugin || !installedPlugin.enabled) {
      return
    }

    activePluginId.value = pluginId

    if (installedPlugin.plugin.onActivate) {
      try {
        const context = createPluginContext(pluginId)
        await installedPlugin.plugin.onActivate(context)
      }
      catch (error) {
        console.error(`❌ 插件 ${installedPlugin.plugin.metadata.name} onActivate 执行失败:`, error)
        addPluginError(pluginId, '激活', error)
      }
    }
  }

  // ===== 失活插件 =====
  async function deactivatePlugin(pluginId: string): Promise<void> {
    const installedPlugin = installedPlugins.value.get(pluginId)
    if (!installedPlugin) {
      return
    }

    if (activePluginId.value === pluginId) {
      activePluginId.value = null
    }

    if (installedPlugin.plugin.onDeactivate) {
      try {
        const context = createPluginContext(pluginId)
        await installedPlugin.plugin.onDeactivate(context)
      }
      catch (error) {
        console.error(`❌ 插件 ${installedPlugin.plugin.metadata.name} onDeactivate 执行失败:`, error)
        addPluginError(pluginId, '停用', error)
      }
    }
  }

  // ===== 从URL安装插件 =====
  async function installPluginFromUrl(url: string): Promise<void> {
    try {
      showInfo('正在加载插件，请稍候...')

      // 检查是否是 ZIP 文件
      if (url.endsWith('.zip')) {
        await installPluginFromZip(url)
        return
      }

      // 检查是否是 .vue 目录格式（以 / 结尾或包含 manifest.json）
      if (url.endsWith('/') || url.includes('/manifest.json')) {
        const baseUrl = url.endsWith('/') ? url : url.replace('/manifest.json', '/')
        await installPluginFromVueDir(baseUrl)
        return
      }

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch plugin: ${response.statusText}`)
      }

      const pluginPackage = normalizePluginPackage(await response.json() as PluginPackage)

      // 动态加载插件代码
      const plugin = await loadPluginFromPackage(pluginPackage)

      await installPlugin(plugin, pluginPackage, 'url', url)
    }
    catch (error) {
      console.error('Failed to install plugin from URL:', error)
      showError(`从URL安装插件失败：${error instanceof Error ? error.message : '未知错误'}`)
      throw error
    }
  }

  // ===== 从ZIP安装插件 =====
  async function installPluginFromZip(url: string): Promise<void> {
    try {
      showInfo('正在下载并解压插件...')

      // 下载ZIP文件
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch plugin ZIP: ${response.statusText}`)
      }

      const zipData = await response.arrayBuffer()
      const zip = await JSZip.loadAsync(zipData)

      // 读取 manifest.json
      const manifestFile = zip.file('manifest.json')
      if (!manifestFile) {
        throw new Error('Invalid plugin ZIP: missing manifest.json')
      }
      const manifestContent = await manifestFile.async('string')
      const manifest = JSON.parse(manifestContent)

      // 读取入口 .vue 文件
      const entryFile = manifest.entry || 'index.vue'
      const vueFile = zip.file(entryFile)
      if (!vueFile) {
        throw new Error(`Invalid plugin ZIP: missing entry file ${entryFile}`)
      }
      const vueContent = await vueFile.async('string')

      // 解析 .vue 文件
      const { template, script, style } = parseVueSFC(vueContent)

      // 构建插件包
      const pluginPackage: PluginPackage = {
        metadata: {
          id: manifest.id,
          name: manifest.name,
          version: manifest.version,
          description: manifest.description,
          author: manifest.author,
          icon: manifest.icon,
          category: manifest.category || 'other',
          permissions: normalizePluginPermissions(manifest.permissions),
          location: normalizePluginLocations(manifest.location),
          api: manifest.api || '2.0',
          window: normalizePluginWindowOptions(manifest.window),
        },
        component: {
          template,
          script,
        },
        vueStyle: style,
        lifecycle: manifest.lifecycle || undefined,
      }

      // 加载插件
      const plugin = await loadPluginFromPackage(pluginPackage)

      await installPlugin(plugin, pluginPackage, 'url', url)
    }
    catch (error) {
      console.error('Failed to install plugin from ZIP:', error)
      showError(`从ZIP安装插件失败：${error instanceof Error ? error.message : '未知错误'}`)
      throw error
    }
  }

  // ===== 从 .vue 目录安装插件 =====
  async function fetchPluginPackageFromVueDir(baseUrl: string): Promise<PluginPackage> {
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
    const manifestResponse = await fetch(`${normalizedBaseUrl}manifest.json`)
    if (!manifestResponse.ok)
      throw new Error(`Failed to fetch manifest: ${manifestResponse.statusText}`)
    const manifest = await manifestResponse.json()
    const entryFile = manifest.entry || 'index.vue'
    const vueResponse = await fetch(`${normalizedBaseUrl}${entryFile}`)
    if (!vueResponse.ok)
      throw new Error(`Failed to fetch Vue component: ${vueResponse.statusText}`)
    const { template, script, style } = parseVueSFC(await vueResponse.text())
    return normalizePluginPackage({
      metadata: {
        id: manifest.id,
        name: manifest.name,
        version: manifest.version,
        description: manifest.description,
        author: manifest.author,
        icon: manifest.icon,
        category: manifest.category || 'other',
        permissions: normalizePluginPermissions(manifest.permissions),
        location: normalizePluginLocations(manifest.location),
        api: manifest.api || '2.0',
        window: normalizePluginWindowOptions(manifest.window),
      },
      component: { template, script },
      vueStyle: style,
      lifecycle: manifest.lifecycle || undefined,
    })
  }

  async function installPluginFromVueDir(baseUrl: string): Promise<void> {
    try {
      showInfo('正在加载 Vue 插件，请稍候...')
      const pluginPackage = await fetchPluginPackageFromVueDir(baseUrl)
      const plugin = await loadPluginFromPackage(pluginPackage)

      await installPlugin(plugin, pluginPackage, 'url', baseUrl)
    }
    catch (error) {
      console.error('Failed to install plugin from Vue directory:', error)
      showError(`从 Vue 目录安装插件失败：${error instanceof Error ? error.message : '未知错误'}`)
      throw error
    }
  }

  // ===== 解析 Vue SFC =====
  function parseVueSFC(content: string): { template: string, script: string, style: string } {
    // 提取 <template> 内容
    // 注意：模板内部可能包含 <template v-if> 等嵌套标签，需要正确匹配最外层的 template 标签
    let template = ''

    // 找到第一个顶级 <template> 标签的位置（可能带属性，但不是 <template v-if 等）
    // 顶级 template 标签通常在行首或只有空白字符
    const templateStartMatch = content.match(/^[ \t]*<template(\s[^>]*)?>|<template(\s[^>]*)?>/m)
    if (templateStartMatch) {
      const templateStartIndex = content.indexOf(templateStartMatch[0])
      const actualTemplateStart = content.indexOf('<template', templateStartIndex)
      const templateContentStart = content.indexOf('>', actualTemplateStart) + 1

      // 使用计数器方法找到匹配的 </template>
      // 从 templateContentStart 开始，计算 <template 和 </template> 的嵌套层级
      let depth = 1
      let pos = templateContentStart
      let templateEndIndex = -1

      while (pos < content.length && depth > 0) {
        const nextOpen = content.indexOf('<template', pos)
        const nextClose = content.indexOf('</template>', pos)

        if (nextClose === -1) {
          // 没有找到关闭标签，退出
          break
        }

        if (nextOpen !== -1 && nextOpen < nextClose) {
          // 找到了一个嵌套的 <template，检查它是否是真正的 template 标签
          // 检查是否是 <template> 或 <template ...> 而不是其他标签如 <templateX>
          const afterOpen = content.charAt(nextOpen + 9) // '<template'.length = 9
          if (afterOpen === '>' || afterOpen === ' ' || afterOpen === '\n' || afterOpen === '\r' || afterOpen === '\t') {
            depth++
          }
          pos = nextOpen + 9
        }
        else {
          // 找到了 </template>
          depth--
          if (depth === 0) {
            templateEndIndex = nextClose
            break
          }
          pos = nextClose + 11 // '</template>'.length = 11
        }
      }

      if (templateEndIndex !== -1) {
        template = content.substring(templateContentStart, templateEndIndex).trim()
      }
    }

    // 提取 <script> 内容
    const scriptMatch = content.match(/<script[^>]*>([\s\S]*?)<\/script>/)
    let script = scriptMatch ? scriptMatch[1].trim() : 'export default {}'

    // 将 export default 转换为可执行的格式
    // 移除 export default，保留对象定义
    script = script.replace(/export\s+default\s+/, '')

    // 提取 <style> 内容
    const styleMatch = content.match(/<style[^>]*>([\s\S]*?)<\/style>/)
    const style = styleMatch ? styleMatch[1].trim() : ''

    console.info('📝 Vue SFC 解析结果:')
    console.info('  - 模板长度:', template.length)
    console.info('  - 脚本长度:', script.length)
    console.info('  - 样式长度:', style.length)
    console.info('  - 模板预览:', template.substring(0, 200))

    return { template, script, style }
  }

  // ===== 从JSON包安装插件 =====
  async function installPluginFromPackage(pluginPackage: PluginPackage, options?: { silent?: boolean, replaceExisting?: boolean, transient?: boolean }): Promise<void> {
    const normalizedPackage = normalizePluginPackage(pluginPackage)
    const plugin = await loadPluginFromPackage(normalizedPackage)

    await installPlugin(plugin, normalizedPackage, 'local', undefined, options)
  }

  // ===== 导出插件 =====
  function exportPlugin(pluginId: string): PluginPackage | null {
    const installedPlugin = installedPlugins.value.get(pluginId)
    if (!installedPlugin) {
      return null
    }

    // 尝试从缓存获取原始插件包数据（IndexedDB）
    const cachedPackage = pluginCache.value[pluginId]
    if (cachedPackage) {
      return normalizePluginPackage(cachedPackage)
    }

    // 如果没有缓存，返回基本结构（无法导出组件代码）
    return normalizePluginPackage({
      metadata: installedPlugin.plugin.metadata,
      component: {
        template: '', // 无法从编译后的组件恢复模板
        script: '',
      },
      configSchema: installedPlugin.plugin.config?.schema,
    })
  }

  // ===== 工具函数 =====

  function validatePlugin(plugin: Plugin): void {
    if (!plugin.metadata?.id) {
      throw new Error('Plugin must have a valid ID')
    }
    if (!plugin.metadata?.name) {
      throw new Error('Plugin must have a name')
    }
    if (!plugin.component) {
      throw new Error('Plugin must have a component')
    }
  }

  async function loadPluginFromPackage(pkg: PluginPackage): Promise<Plugin> {
    const normalizedPackage = normalizePluginPackage(pkg)
    assertSupportedPluginApiVersion(normalizedPackage.metadata.api, normalizedPackage.metadata.name)

    // Vue 组件格式：pkg.component 包含 template 和 script
    if (!('component' in normalizedPackage) || !normalizedPackage.component) {
      throw new Error('Invalid plugin package: missing component (template/script)')
    }

    const scriptContent = normalizedPackage.component.script || '{}'

    // 使用 Function 构造器执行 script 并获取组件定义
    // 这比 import() Blob URL 更可靠，兼容性更好
    let componentDef: Record<string, any> = {}
    try {
      // eslint-disable-next-line no-new-func -- 插件系统需要动态执行代码
      const getComponentDef = new Function(`return ${scriptContent}`)
      componentDef = getComponentDef() || {}
    }
    catch (evalError) {
      console.error('解析插件 script 失败:', evalError)
      console.warn('Script 内容预览:', scriptContent.substring(0, 200))
      throw new Error(`插件 script 解析失败: ${evalError instanceof Error ? evalError.message : '语法错误'}`)
    }

    // 创建Vue组件，合并props定义，注入 UI 库组件
    const component = markRaw(defineComponent({
      ...componentDef,
      template: normalizedPackage.component.template,
      components: {
        // 注入 @proj-airi/ui 组件，M 前缀避免与原生 HTML 标签冲突
        MIcon: PluginIcon,
        MCheckbox: Checkbox,
        MCombobox: Combobox,
        MInput: Input,
        MInputFile: InputFile,
        MBasicInputFile: BasicInputFile,
        MInputKeyValue: InputKeyValue,
        MRadio: Radio,
        MRange: Range,
        MRoundRange: RoundRange,
        MColorHueRange: ColorHueRange,
        MSelect: Select,
        MOption: SelectOption,
        MTextarea: Textarea,
        MBasicTextarea: BasicTextarea,
        MFieldCheckbox: FieldCheckbox,
        MFieldInput: FieldInput,
        MFieldKeyValues: FieldKeyValues,
        MFieldRange: FieldRange,
        MFieldSelect: FieldSelect,
        MFieldValues: FieldValues,
        MBidirectionalTransition: BidirectionalTransition,
        MTransitionHorizontal: TransitionHorizontal,
        MTransitionVertical: TransitionVertical,
        // 允许插件自定义组件覆盖
        ...componentDef.components,
      },
      props: {
        ...componentDef.props,
        context: {
          type: Object,
          required: true,
        },
        minimized: {
          type: Boolean,
          default: false,
        },
      },
    }))

    // 创建生命周期函数
    const lifecycle = normalizedPackage.lifecycle || {}
    const createLifecycleFunc = (code?: string) => {
      if (!code)
        return undefined
      // eslint-disable-next-line no-new-func -- 插件系统需要动态执行代码
      return new Function('context', code) as any
    }

    const plugin: Plugin = {
      metadata: normalizedPackage.metadata,
      component,
      config: normalizedPackage.configSchema ? { schema: normalizedPackage.configSchema } : undefined,
      onLoad: createLifecycleFunc(lifecycle.onLoad),
      onActivate: createLifecycleFunc(lifecycle.onActivate),
      onDeactivate: createLifecycleFunc(lifecycle.onDeactivate),
      onUnload: createLifecycleFunc(lifecycle.onUnload),
    }

    return plugin
  }

  function saveInstalledPlugins(): void {
    const pluginsData = Array.from(installedPlugins.value.entries())
      .filter(([, installedPlugin]) => !installedPlugin.transient)
      .map(([id, installedPlugin]) => {
        const basePackage: PluginPackage = {
          metadata: installedPlugin.plugin.metadata,
          component: {
            template: '', // 无法序列化组件，需要保存原始数据
            script: '',
          },
          configSchema: installedPlugin.plugin.config?.schema,
        }

        const cachedPackage = pluginCache.value[id]
        const pluginPackage = normalizePluginPackage({
          ...basePackage,
          ...(cachedPackage || {}),
          metadata: {
            ...(cachedPackage?.metadata || {}),
            ...installedPlugin.plugin.metadata,
          },
        })

        return {
          id,
          enabled: installedPlugin.enabled,
          installedAt: installedPlugin.installedAt,
          updatedAt: installedPlugin.updatedAt,
          source: installedPlugin.source,
          sourceUrl: installedPlugin.sourceUrl,
          metadata: pluginPackage.metadata,
          pluginPackage,
        }
      })

    // 保存到 IndexedDB（自动持久化）
    pluginsDataPersisted.value = pluginsData
  }

  async function refreshPluginPackageFromSource(sourceUrl: string | undefined, current: PluginPackage): Promise<PluginPackage> {
    if (!sourceUrl || sourceUrl.endsWith('.zip') || (!sourceUrl.endsWith('/') && !sourceUrl.endsWith('/manifest.json')))
      return current
    const baseUrl = sourceUrl.endsWith('/') ? sourceUrl : sourceUrl.replace(/manifest\.json$/, '')
    try {
      const manifestResponse = await fetch(`${baseUrl}manifest.json`)
      if (!manifestResponse.ok)
        return current
      const manifest = await manifestResponse.json() as { id?: string, version?: string }
      if (manifest.id !== current.metadata.id || String(manifest.version ?? '') === String(current.metadata.version ?? ''))
        return current
      const refreshed = await fetchPluginPackageFromVueDir(baseUrl)
      console.info(`🔄 已更新插件 ${refreshed.metadata.name}：${current.metadata.version ?? '未知'} → ${refreshed.metadata.version ?? '未知'}`)
      return refreshed
    }
    catch (error) {
      console.warn(`⚠️ 插件 ${current.metadata.name} 源码更新失败，继续使用缓存版本:`, error)
      return current
    }
  }

  async function loadInstalledPlugins(): Promise<void> {
    const pluginsData = pluginsDataPersisted.value
    if (!pluginsData || pluginsData.length === 0) {
      return
    }

    try {
      const typedData = pluginsData as Array<{
        id: string
        enabled: boolean
        installedAt: number
        updatedAt: number
        source: 'builtin' | 'url' | 'local'
        sourceUrl?: string
        metadata: any
        pluginPackage?: PluginPackage
      }>

      console.info(`📦 正在恢复 ${typedData.length} 个已安装插件...`)

      // 恢复插件
      for (const pluginData of typedData) {
        try {
          // 优先使用 pluginCache 中的完整数据（包含 lifecycle）
          let pkgToLoad = pluginData.pluginPackage
          const cachedPkg = pluginCache.value[pluginData.id]
          if (cachedPkg) {
            // 合并：缓存中的数据更完整（特别是 lifecycle 字段）
            pkgToLoad = { ...pkgToLoad, ...cachedPkg }
          }

          // 如果有保存的插件包数据，直接加载
          if (pkgToLoad) {
            // 旧安装记录可能没有 sourceUrl，内置插件统一从当前前端资源目录检查版本。
            // 不是内置资源的插件若不存在该路径会安静回退到缓存。
            const sourceUrl = pluginData.sourceUrl ?? `/plugins/${pluginData.id}/`
            const refreshedPackage = await refreshPluginPackageFromSource(sourceUrl, pkgToLoad)
            if (refreshedPackage !== pkgToLoad) {
              pkgToLoad = refreshedPackage
              pluginCache.value = { ...pluginCache.value, [pluginData.id]: pkgToLoad }
            }
            // 如果缓存中缺少 lifecycle.onLoad 数据（旧版本安装的插件），尝试从 sourceUrl 或默认路径重新获取
            if (!pkgToLoad.lifecycle?.onLoad) {
              // 构建可能的 manifest URL 列表
              const manifestUrls: string[] = []
              if (pluginData.sourceUrl) {
                const baseUrl = pluginData.sourceUrl.endsWith('/') ? pluginData.sourceUrl : `${pluginData.sourceUrl}/`
                manifestUrls.push(`${baseUrl}manifest.json`)
              }
              // 回退：尝试从 public/plugins/{id}/ 目录加载
              manifestUrls.push(`/plugins/${pluginData.id}/manifest.json`)

              console.info(`🔄 插件 ${pluginData.metadata.name} 缺少 lifecycle.onLoad，sourceUrl=${pluginData.sourceUrl || '(空)'}，尝试恢复...`)

              for (const manifestUrl of manifestUrls) {
                try {
                  console.info(`  尝试: ${manifestUrl}`)
                  const resp = await fetch(manifestUrl)
                  if (resp.ok) {
                    const manifest = await resp.json()
                    if (manifest.lifecycle?.onLoad) {
                      pkgToLoad.lifecycle = manifest.lifecycle
                      // 更新缓存，避免下次还要重新获取
                      pluginCache.value = { ...pluginCache.value, [pluginData.id]: { ...pkgToLoad } }
                      console.info(`✅ 插件 ${pluginData.metadata.name} lifecycle 已从 ${manifestUrl} 恢复`)
                      break
                    }
                  }
                }
                catch (fetchErr) {
                  console.warn(`  ⚠️ 从 ${manifestUrl} 恢复失败:`, fetchErr)
                }
              }
            }

            console.info(`🔄 恢复插件 ${pluginData.metadata.name}，lifecycle.onLoad: ${pkgToLoad.lifecycle?.onLoad ? `有(${pkgToLoad.lifecycle.onLoad.length}字符)` : '无'}`)

            const plugin = await loadPluginFromPackage(pkgToLoad)
            const installedPlugin: InstalledPlugin = {
              plugin,
              enabled: pluginData.enabled,
              installedAt: pluginData.installedAt,
              updatedAt: pluginData.updatedAt,
              source: pluginData.source,
              sourceUrl: pluginData.sourceUrl,
            }

            installedPlugins.value.set(pluginData.id, installedPlugin)

            // 如果插件已启用，调用 onLoad 注册自定义消息类型等
            if (pluginData.enabled) {
              injectPluginStyle(pkgToLoad)
            }

            if (pluginData.enabled && plugin.onLoad) {
              try {
                const ctx = createPluginContext(pluginData.id)
                await plugin.onLoad(ctx)
                console.info(`✅ 插件 ${plugin.metadata.name} onLoad 执行成功，当前自定义消息类型数: ${customMessageTypes.value.size}`)
              }
              catch (onLoadError) {
                console.error(`❌ 插件 ${plugin.metadata.name} onLoad 执行失败:`, onLoadError)
                addPluginError(pluginData.id, '加载', onLoadError)
              }
            }
            else if (pluginData.enabled) {
              console.warn(`⚠️ 插件 ${pluginData.metadata.name} 已启用但没有 onLoad 钩子`)
            }

            console.info(`✅ 已恢复插件: ${plugin.metadata.name}`)
          }
          else if (pluginData.sourceUrl) {
            // 如果有源 URL，尝试重新加载
            console.warn(`⚠️ 插件 ${pluginData.metadata.name} 需要从 URL 重新加载`)
          }
          else {
            console.warn(`⚠️ 插件 ${pluginData.metadata.name} 无法恢复（缺少插件包数据）`)
          }
        }
        catch (error) {
          console.error(`❌ 恢复插件 ${pluginData.metadata.name} 失败:`, error)
          addPluginError(pluginData.id, '恢复', error)
        }
      }

      // 恢复完成后重新保存，确保 lifecycle 等补全的数据被持久化
      saveInstalledPlugins()
    }
    catch (error) {
      console.error('Failed to load installed plugins:', error)
    }

    // 启动期清理：工作台预览插件标记为 transient 且使用 __mwp_ 前缀。
    // 正常路径下 saveInstalledPlugins 会跳过 transient，但若预览过程中崩溃或异步竞争
    // 让 transient: false 的快照被写回，需要在启动时强制 uninstall 这些残留。
    purgeTransientPlugins()
  }

  /**
   * 清理所有 transient 预览插件（含工作台 __mwp_ 后缀的预览实例）。
   * 静默执行，不弹 toast，不写错误。
   */
  function purgeTransientPlugins(): void {
    const idsToPurge: string[] = []
    for (const [id, ip] of installedPlugins.value.entries()) {
      if (ip.transient || /__mwp_[a-z0-9]+__/i.test(id) || id.endsWith('__workshop_preview'))
        idsToPurge.push(id)
    }
    for (const id of idsToPurge) {
      try {
        // fire-and-forget：避免启动阻塞，预览插件没有副作用值得 await
        void uninstallPlugin(id, { silent: true, preserveConfig: false } as Parameters<typeof uninstallPlugin>[1])
      }
      catch { /* ignore */ }
    }
    if (idsToPurge.length > 0)
      console.info(`🧹 已清理 ${idsToPurge.length} 个工作台预览残留插件`)
  }

  async function initializePlugin(plugin: Plugin, context: PluginContext): Promise<void> {
    if (plugin.onInit) {
      await plugin.onInit(context)
    }
    if (plugin.onLoad) {
      await plugin.onLoad(context)
    }
  }

  function getPluginStyleContent(pluginId: string): string | null {
    return document.getElementById(`plugin-style-${pluginId}`)?.textContent ?? null
  }

  function restorePluginStyle(pluginId: string, style: string | null): void {
    if (style === null) {
      const styleEl = document.getElementById(`plugin-style-${pluginId}`)
      if (styleEl) {
        styleEl.remove()
      }
      return
    }

    let styleEl = document.getElementById(`plugin-style-${pluginId}`)
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = `plugin-style-${pluginId}`
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = style
  }

  function injectPluginStyle(pluginPackage: PluginPackage): void {
    const normalizedPackage = normalizePluginPackage(pluginPackage)
    restorePluginStyle(normalizedPackage.metadata.id, normalizedPackage.vueStyle || null)
  }

  function cleanupPluginRuntime(pluginId: string): void {
    beforeAIReplyHandlers.value.delete(pluginId)
    beforeUserMessageHandlers.value.delete(pluginId)
    afterUserMessageHandlers.value.delete(pluginId)
    contactSwitchHandlers.value.delete(pluginId)
    dynamicPromptGenerators.value.delete(pluginId)
    injectedSystemPrompts.value.delete(pluginId)
    if (aiActionRegistry.value.has(pluginId)) {
      const registry = new Map(aiActionRegistry.value)
      registry.delete(pluginId)
      aiActionRegistry.value = registry
    }

    const registeredTypes = pluginMessageTypeMap.value.get(pluginId)
    if (registeredTypes && registeredTypes.size > 0) {
      const newMap = new Map(customMessageTypes.value)
      for (const typeName of registeredTypes) {
        newMap.delete(typeName)
      }
      customMessageTypes.value = newMap
      pluginMessageTypeMap.value.delete(pluginId)
      console.info(`🧹 已清理插件 ${pluginId} 注册的 ${registeredTypes.size} 个消息类型`)
    }

    const styleEl = document.getElementById(`plugin-style-${pluginId}`)
    if (styleEl) {
      styleEl.remove()
    }
  }

  function cleanupPluginConfig(pluginId: string): void {
    // 清理配置和私有存储（IndexedDB）
    pluginKV.removeByPrefix(`${CONFIG_STORAGE_PREFIX}${pluginId}/`)
    pluginKV.removeByPrefix(`${PLUGIN_DATA_PREFIX}${pluginId}/`)
  }

  // ===== 初始化 =====
  // 注意：loadInstalledPlugins 现在在 IndexedDB 存储就绪后自动调用
  const updatablePluginsCount = ref(0)

  return {
    // 状态
    installedPlugins: computed(() => Array.from(installedPlugins.value.values())),
    updatablePluginsCount,
    enabledPlugins,
    pluginsByLocation,
    activePluginId,
    customMessageTypes: computed(() => Array.from(customMessageTypes.value.values())),
    injectedSystemPrompts: computed(() => Array.from(injectedSystemPrompts.value.values())),
    availableAIActions,

    // 钩子处理器（供 chat store 调用）
    beforeAIReplyHandlers: computed(() => Array.from(beforeAIReplyHandlers.value.values())),
    beforeUserMessageHandlers: computed(() => Array.from(beforeUserMessageHandlers.value.values())),
    afterUserMessageHandlers: computed(() => Array.from(afterUserMessageHandlers.value.values())),
    contactSwitchHandlers: computed(() => Array.from(contactSwitchHandlers.value.values())),
    dynamicPromptGenerators: computed(() => Array.from(dynamicPromptGenerators.value.values())),

    // 方法
    installPlugin,
    uninstallPlugin,
    togglePlugin,
    activatePlugin,
    deactivatePlugin,
    installPluginFromUrl,
    installPluginFromPackage,
    purgeTransientPlugins,
    exportPlugin,
    createPluginContext,
    executeAIAction,

    // 错误追踪
    pluginErrors: computed(() => pluginErrors.value),
    addPluginError,
    getPluginErrors,
    clearPluginErrors,
  }
})
